// 🤖 useAssistant - Hook para la lógica del asistente virtual
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { Message } from '@/components/assistant/AssistantChat';

interface AssistantCommand {
  pattern: RegExp;
  action: (match: RegExpMatchArray, context: AssistantContext) => AssistantResponse;
  description: string;
}

interface AssistantContext {
  currentPath: string;
  currentModule: string;
  navigate: (path: string) => void;
}

interface AssistantResponse {
  message: string;
  action?: () => void;
  suggestions?: string[];
}

export function useAssistant() {
  const location = useLocation();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  // Detectar módulo actual
  const getCurrentModule = useCallback(() => {
    const path = location.pathname;
    if (path.includes('/crud-factory')) return 'CRUD Factory';
    if (path.includes('/usuarios')) return 'Usuarios';
    if (path.includes('/empresas')) return 'Empresas';
    if (path.includes('/dashboard')) return 'Dashboard';
    if (path.includes('/settings')) return 'Configuración';
    return 'Inicio';
  }, [location.pathname]);

  // Comandos disponibles (memoizado: solo cierran sobre el context que reciben)
  const commands: AssistantCommand[] = useMemo(() => [
    // Navegación
    {
      pattern: /(?:ir|navegar|llévame|abre|abrir|mostrar)\s+(?:a|al|a la)?\s*(.+)/i,
      description: 'Navegar a un módulo',
      action: (match, context) => {
        const destination = match[1].toLowerCase().trim();

        const routes: Record<string, { path: string; name: string }> = {
          'dashboard': { path: '/dashboard', name: 'Dashboard' },
          'inicio': { path: '/dashboard', name: 'Dashboard' },
          'usuarios': { path: '/usuarios', name: 'Usuarios' },
          'empresas': { path: '/empresas', name: 'Empresas' },
          'crud factory': { path: '/crud-factory', name: 'CRUD Factory' },
          'factory': { path: '/crud-factory', name: 'CRUD Factory' },
          'configuración': { path: '/settings', name: 'Configuración' },
          'ajustes': { path: '/settings', name: 'Configuración' },
          'settings': { path: '/settings', name: 'Configuración' },
        };

        for (const [key, route] of Object.entries(routes)) {
          if (destination.includes(key)) {
            return {
              message: `✅ Navegando a ${route.name}...`,
              action: () => context.navigate(route.path),
              suggestions: ['¿Qué más puedo hacer?', 'Ayuda', 'Comandos disponibles'],
            };
          }
        }

        return {
          message: `❌ No encontré el módulo "${destination}". Intenta con: Dashboard, Usuarios, Empresas, CRUD Factory o Configuración.`,
          suggestions: ['Ir a Dashboard', 'Ir a Usuarios', 'Ir a CRUD Factory'],
        };
      },
    },

    // Crear registro
    {
      pattern: /(?:crear|nuevo|agregar|añadir)\s+(?:un|una)?\s*(.+)/i,
      description: 'Crear un nuevo registro',
      action: (match, context) => {
        const entity = match[1].toLowerCase().trim();

        if (entity.includes('usuario')) {
          return {
            message: '✅ Abriendo formulario para crear un nuevo usuario...',
            action: () => {
              context.navigate('/usuarios');
              setTimeout(() => {
                const createButton = document.querySelector('[data-action="create"]') as HTMLButtonElement;
                createButton?.click();
              }, 500);
            },
          };
        }

        if (entity.includes('empresa')) {
          return {
            message: '✅ Abriendo formulario para crear una nueva empresa...',
            action: () => {
              context.navigate('/empresas');
              setTimeout(() => {
                const createButton = document.querySelector('[data-action="create"]') as HTMLButtonElement;
                createButton?.click();
              }, 500);
            },
          };
        }

        return {
          message: `Puedo ayudarte a crear: usuarios, empresas. ¿Cuál te gustaría crear?`,
          suggestions: ['Crear usuario', 'Crear empresa'],
        };
      },
    },

    // Búsqueda
    {
      pattern: /(?:buscar|encontrar|busca|encuentra)\s+(.+)/i,
      description: 'Buscar registros',
      action: (match) => {
        const query = match[1].trim();
        return {
          message: `🔍 Buscando "${query}"... Por favor, ve al módulo correspondiente y usa la búsqueda por voz o el input de búsqueda.`,
          suggestions: ['Ir a Usuarios', 'Ir a Empresas'],
        };
      },
    },

    // Exportar
    {
      pattern: /(?:exportar|descargar|exporta|descarga)\s+(?:a|en)?\s*(excel|pdf)?/i,
      description: 'Exportar datos',
      action: (match) => {
        const format = match[1]?.toLowerCase() || 'excel';
        return {
          message: `📊 Para exportar a ${format.toUpperCase()}, ve al módulo que deseas exportar y haz click en el botón "${format.toUpperCase()}".`,
          suggestions: ['Ir a Usuarios', 'Ir a Empresas'],
        };
      },
    },

    // Ayuda
    {
      pattern: /(?:ayuda|help|comandos|qué puedes hacer|que puedes hacer)/i,
      description: 'Mostrar ayuda',
      action: () => ({
        message: `🤖 **Comandos disponibles:**

📍 **Navegación:**
- "Ir a Dashboard"
- "Llévame a Usuarios"
- "Abre CRUD Factory"

➕ **Crear:**
- "Crear usuario"
- "Nuevo registro de empresa"

🔍 **Buscar:**
- "Buscar Juan"
- "Encontrar Empresa Demo"

📊 **Exportar:**
- "Exportar a Excel"
- "Descargar PDF"

💡 **Consejos:**
- Usa el micrófono para comandos de voz
- Puedo entender lenguaje natural
- Pregúntame sobre el módulo actual`,
        suggestions: ['Ir a Dashboard', 'Crear usuario', '¿Dónde estoy?'],
      }),
    },

    // Contexto actual
    {
      pattern: /(?:dónde estoy|donde estoy|módulo actual|página actual)/i,
      description: 'Mostrar contexto actual',
      action: (_, context) => ({
        message: `📍 Estás en: **${context.currentModule}**\n\nRuta: ${context.currentPath}`,
        suggestions: ['Ayuda', 'Ir a Dashboard', 'Comandos disponibles'],
      }),
    },

    // Saludo
    {
      pattern: /^(?:hola|hi|hey|buenas|buenos días|buenas tardes|buenas noches)$/i,
      description: 'Saludo',
      action: () => ({
        message: `👋 ¡Hola! Soy tu asistente virtual.\n\n¿En qué puedo ayudarte hoy?`,
        suggestions: ['Ayuda', 'Ir a Dashboard', '¿Qué puedes hacer?'],
      }),
    },

    // Gracias
    {
      pattern: /^(?:gracias|thanks|thank you|muchas gracias)$/i,
      description: 'Agradecimiento',
      action: () => ({
        message: `😊 ¡De nada! Estoy aquí para ayudarte cuando lo necesites.`,
        suggestions: ['Ayuda', 'Comandos disponibles'],
      }),
    },
  ], []);

  // Procesar mensaje del usuario
  const processMessage = useCallback(async (userMessage: string) => {
    setIsProcessing(true);

    // Agregar mensaje del usuario
    const userMsg: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: userMessage,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);

    // Simular delay de procesamiento
    await new Promise((resolve) => setTimeout(resolve, 500));

    const context: AssistantContext = {
      currentPath: location.pathname,
      currentModule: getCurrentModule(),
      navigate,
    };

    // Buscar comando que coincida
    let response: AssistantResponse | null = null;
    for (const command of commands) {
      const match = userMessage.match(command.pattern);
      if (match) {
        response = command.action(match, context);
        break;
      }
    }

    // Si no hay comando, respuesta por defecto
    if (!response) {
      response = {
        message: `🤔 No entendí tu mensaje. Intenta con:\n\n- "Ir a Dashboard"\n- "Crear usuario"\n- "Ayuda"\n- "¿Qué puedes hacer?"`,
        suggestions: ['Ayuda', 'Comandos disponibles', 'Ir a Dashboard'],
      };
    }

    // Agregar mensaje del asistente
    const assistantMsg: Message = {
      id: (Date.now() + 1).toString(),
      type: 'assistant',
      content: response.message,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, assistantMsg]);

    // Actualizar sugerencias
    if (response.suggestions) {
      setSuggestions(response.suggestions);
    }

    // Ejecutar acción si existe
    if (response.action) {
      setTimeout(() => {
        response.action!();
        toast.success('Acción ejecutada');
      }, 800);
    }

    setIsProcessing(false);
  }, [location.pathname, getCurrentModule, navigate, commands]);

  // Sugerencias contextuales al abrir el chat
  useEffect(() => {
    const module = getCurrentModule();
    const contextSuggestions = [
      `¿Qué puedo hacer en ${module}?`,
      'Ayuda',
      'Comandos disponibles',
    ];
    setSuggestions(contextSuggestions);
  }, [getCurrentModule]);

  // Mensaje de bienvenida inicial:
  // No se agrega automáticamente; el usuario lo verá en el UI vacío.
  useEffect(() => {
    // Reservado para inicialización futura del asistente.
  }, []);

  return {
    messages,
    isProcessing,
    suggestions,
    currentModule: getCurrentModule(),
    processMessage,
    clearMessages: () => setMessages([]),
  };
}
